// src/pages/Dashboard.js - With caching implementation
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Grid, 
  Box, 
  Card, 
  Typography, 
  Chip, 
  IconButton, 
  alpha, 
  useTheme,
  Divider,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Storage as ServerIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Article as LogIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Public as RegionIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  DisplaySettings as ProductIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Import required Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Import custom hooks and components
import { useAppData } from '../contexts/AppDataContext';
import { useAuth } from '../hooks/useAuth';
import DashboardContainer from '../components/common/DashboardContainer';

// Import role utilities
import { isFinanceRole, isRDRole, isAdminRole } from '../utils/roleUtils';

// Import client name mapping
import { getFullClientName, isExcludedInternalClient, countClientsParentViewDeduped, countFromClientProductCountRegions } from '../utils/clientNameMappings';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

// Helper function to get colors
function getColor(index, alpha = 1) {
  const colors = [
    `rgba(58, 134, 255, ${alpha})`,   // blue - #3a86ff (primary)
    `rgba(76, 201, 189, ${alpha})`,   // teal - #4cc9bd (secondary)
    `rgba(255, 183, 77, ${alpha})`,   // amber - #ffb74d (tertiary)
    `rgba(255, 153, 102, ${alpha})`,  // soft orange - #ff9966 (quaternary)
    `rgba(76, 175, 80, ${alpha})`,    // green
    `rgba(66, 165, 245, ${alpha})`,   // light blue
    `rgba(126, 87, 194, ${alpha})`,   // purple
    `rgba(240, 98, 146, ${alpha})`,   // pink
    `rgba(103, 58, 183, ${alpha})`,   // deep purple
    `rgba(0, 150, 136, ${alpha})`,    // teal
  ];
  return colors[index % colors.length];
}

// Modern Metric Card component
// Modified ModernMetricCard component with error handling
const ModernMetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary', onClick }) => {
  const theme = useTheme();
  
  // Safely get color - handle case when color is not in palette
  const getColorSafely = (colorName) => {
    // Special handling for quaternary color which isn't in the palette
    if (colorName === 'quaternary') {
      return '#ff9966'; // This is the quaternary color from getColor function
    }
    
    // Check if the color exists in the theme palette
    if (theme.palette[colorName] && theme.palette[colorName].main) {
      return theme.palette[colorName].main;
    }
    // Fallback to primary color if the specified color doesn't exist
    return theme.palette.primary.main;
  };
  
  // Get alpha color safely
  const getAlphaSafely = (colorName, alphaValue) => {
    const colorMain = getColorSafely(colorName);
    return alpha(colorMain, alphaValue);
  };
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: '16px',
        background: `linear-gradient(145deg, ${getAlphaSafely(color, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-10px)',
          boxShadow: `0 15px 30px ${alpha(theme.palette.common.black, 0.12)}`,
          '& .icon-bg': {
            transform: 'scale(1.1)',
          }
        } : {},
      }}
      onClick={onClick}
    >
      <Box 
        className="icon-bg"
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${getAlphaSafely(color, 0.05)} 0%, ${getAlphaSafely(color, 0.02)} 100%)`,
          transition: 'transform 0.3s ease',
        }}
      />
      <Box sx={{ position: 'relative', height: '100%', p: 3, pb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography 
              variant="h6" 
              fontWeight="medium" 
              sx={{ 
                color: theme.palette.text.primary,
                fontSize: '1.1rem',
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: alpha(theme.palette.text.secondary, 0.8),
                mt: 0.5,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Box 
            sx={{
              backgroundColor: getAlphaSafely(color, 0.1),
              color: getColorSafely(color),
              borderRadius: '12px',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        
        <Box display="flex" alignItems="flex-end" justifyContent="space-between">
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            sx={{ 
              color: getColorSafely(color),
              fontSize: '2.5rem',
            }}
          >
            {value}
          </Typography>
          
          {trend && trendValue && (
            <Chip
              label={
                <Box display="flex" alignItems="center">
                  {trend === 'up' ? '↑' : '↓'}
                  <Typography variant="caption" fontWeight="bold" sx={{ ml: 0.5 }}>
                    {trendValue}
                  </Typography>
                </Box>
              }
              sx={{
                backgroundColor: trend === 'up' 
                  ? alpha(theme.palette.success.main, 0.1) 
                  : alpha(theme.palette.error.main, 0.1),
                color: trend === 'up' 
                  ? theme.palette.success.main 
                  : theme.palette.error.main,
                fontWeight: 'bold',
                borderRadius: '10px',
                height: 24,
              }}
              size="small"
            />
          )}
        </Box>
      </Box>
    </Card>
  );
};
// Modern Chart Card component
const ModernChartCard = ({ title, subtitle, chart, height = 320, actions }) => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const open = Boolean(menuAnchorEl);
  
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '16px',
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: 'blur(8px)',
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="medium">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {actions}
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                ml: 1,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            height: height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
            {chart}
        </Box>
      </Box>
      
      <Menu
        anchorEl={menuAnchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '12px',
            minWidth: 150,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMenuClose}>Refresh</MenuItem>
        <MenuItem onClick={handleMenuClose}>Export as PNG</MenuItem>
        <MenuItem onClick={handleMenuClose}>View Full Screen</MenuItem>
      </Menu>
    </Card>
  );
};

// Main Dashboard Component
export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission } = useAuth(); // Get user and permission check function
  
  // State with defaults to prevent empty data issues
  const [selectedRegion, setSelectedRegion] = useState('');
  const [timeRange, setTimeRange] = useState('24h'); // Keep this for API calls but hide the UI
  const [regionMenuAnchor, setRegionMenuAnchor] = useState(null);
  
  // Destructure only what we need from AppDataContext
  const {
    summary,
    alerts,
    systemMetrics,
    clientLogs,
    clients,
    clientLicenses,
    clientCount,
    refreshAllData,
    loading,
    fetchClientCount
  } = useAppData();
  
  // Derive regions from clientCount data to prevent 'regions not defined' error
  const regions = useMemo(() => {
    if (clientCount && clientCount.regions) {
      return Object.keys(clientCount.regions);
    }
    return [];
  }, [clientCount]);
  
  // Check for region in query params on initial load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const regionParam = params.get('region');
    if (regionParam) {
      setSelectedRegion(regionParam);
    }
  }, [location.search]);
  
  // Store selected region in localStorage when it changes
  useEffect(() => {
    if (selectedRegion) {
      localStorage.setItem('selectedRegion', selectedRegion);
    } else {
      localStorage.removeItem('selectedRegion');
    }
  }, [selectedRegion]);
  
  // Handle refresh with force refresh
  const handleRefresh = useCallback(() => {
    console.log("Dashboard refreshing data with params:", { 
      region: selectedRegion, 
      timeRange: timeRange
    });
    
    refreshAllData({ 
      region: selectedRegion, 
      timeRange: timeRange, 
      forceRefresh: true 
    });
  }, [refreshAllData, selectedRegion, timeRange]);
  
  // Handle region menu
  const handleRegionMenuOpen = (event) => {
    setRegionMenuAnchor(event.currentTarget);
  };
  
  const handleRegionMenuClose = () => {
    setRegionMenuAnchor(null);
  };
  
  const handleRegionSelect = (regionCode) => {
    // Set the selected region to filter data instead of navigating
    setSelectedRegion(regionCode);
    handleRegionMenuClose();
    
    // Refresh data with the newly selected region
    refreshAllData({ 
      region: regionCode, 
      timeRange: timeRange, 
      forceRefresh: true 
    });
  };
  
  // Navigation functions
  const navigateToHosts = () => {
    if (selectedRegion) {
      navigate(`/hosts?region=${selectedRegion}`);
    } else {
      navigate('/hosts');
    }
  };
  
  const navigateToClients = () => {
    if (selectedRegion) {
      navigate(`/clients?region=${selectedRegion}`);
    } else {
      navigate('/clients');
    }
  };
  
  const navigateToProducts = () => {
    if (selectedRegion) {
      navigate(`/products?region=${selectedRegion}`);
    } else {
      navigate('/products');
    }
  };
  
  const navigateToAlerts = () => {
    if (selectedRegion) {
      navigate(`/alerts?region=${selectedRegion}`);
    } else {
      navigate('/alerts');
    }
  };
  
  // Memoized time range display text
  const timeRangeText = useMemo(() => {
    switch(timeRange) {
      case '1h': return 'Last Hour';
      case '6h': return 'Last 6 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      default: return 'Last 24 Hours';
    }
  }, [timeRange]);
  
  // Memoized system metrics data calculation
  const systemMetricsData = useMemo(() => {
    if (!systemMetrics || !systemMetrics.cpu || !systemMetrics.cpu.length) {
      return null;
    }
    
    const uniqueHosts = new Set(systemMetrics.cpu.map(item => item.hostname));
    const hostnames = Array.from(uniqueHosts);
    
    // Filter data points to avoid overwhelming charts
    const timeLabels = systemMetrics.cpu
      .map(item => new Date(item.time).toLocaleTimeString())
      .filter((value, index, array) => index % Math.ceil(array.length / 20) === 0);
    
    return {
      labels: timeLabels,
      datasets: hostnames.map((hostname, index) => {
        const hostData = systemMetrics.cpu.filter(item => item.hostname === hostname);
        return {
          label: `${hostname} - CPU`,
          data: hostData.map(item => item.value),
          borderColor: getColor(index),
          backgroundColor: alpha(getColor(index), 0.2),
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
        };
      }),
    };
  }, [systemMetrics]);
  
  // Check if the user has finance role
  const hasFinanceRole = useMemo(() => {
    if (!user) return false;
    
    // Check for different possible formats of role information
    if (user.role === 'finance') return true;
    if (user.roles && user.roles.role_description === 'Finance') return true;
    if (user.roles && Array.isArray(user.roles) && user.roles.includes('finance')) return true;
    
    return false;
  }, [user]);
  
  // Memoized region distribution data calculation
  const regionDistributionData = useMemo(() => {
    // Return null if still loading to show loading indicator
    if (loading && loading.clientCount) {
      return null;
    }
    
    // Add detailed console logging to help debug
    console.log("Processing clientCount data for region distribution");
    
    if (!clientCount) {
      console.warn("clientCount is undefined or null");
      return null;
    }
    
    if (!clientCount.regions) {
      console.warn("clientCount.regions is undefined or null");
      return null;
    }
    
    const regionNames = Object.keys(clientCount.regions);
    console.log("Region names:", regionNames);
    
    if (regionNames.length === 0) {
      console.warn("No regions found in clientCount data");
      return null;
    }
    
    try {
      // Simple calculation without complex filtering to improve performance
      const chartData = {
        labels: regionNames,
        datasets: [
          {
            label: 'Clients',
            data: regionNames.map(region => {
              // Add null check and use a safer calculation approach
              const regionData = clientCount.regions[region];
              if (!regionData) {
                console.warn(`No data for region ${region}`);
                return 0;
              }

              if (!regionData.clients) {
                console.warn(`No clients data for region ${region}`);
                return 0;
              }

              const count = countClientsParentViewDeduped(
                Object.keys(regionData.clients),
                clientLicenses,
                clientLogs
              );
              console.log(`Region ${region} has ${count} clients (parent + canonical dedupe)`);
              return count;
            }),
            backgroundColor: regionNames.map((_, i) => alpha(getColor(i), 0.7)),
            borderColor: regionNames.map((_, i) => getColor(i)),
            borderWidth: 1,
            hoverOffset: 4,
          },
        ],
      };
      
      console.log("Final chart data:", chartData);
      return chartData;
    } catch (err) {
      console.error("Error processing region distribution data:", err);
      return null;
    }
  }, [clientCount, loading, clientLicenses, clientLogs]);
  
  // Loading state specifically for region distribution
  const isRegionDistributionLoading = loading && loading.clientCount;
  
  // Doughnut options with explicitly disabled animations for better performance
  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
      },
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);
  
  // Calculate derived values for metric cards
  const activeServicesPercentage = useMemo(() => {
    if (!summary || !summary.total_hosts) return 0;
    return Math.round((summary.active_services / summary.total_hosts) * 100);
  }, [summary]);
  
  // Filter alerts by selected region
  const filteredAlerts = useMemo(() => {
    if (!alerts) return [];
    if (!selectedRegion) return alerts;
    return alerts.filter(alert => alert.region === selectedRegion);
  }, [alerts, selectedRegion]);
  
  // Calculate total product count based on region
  const productCount = useMemo(() => {
    if (!clientLogs || !clientLogs.by_product) return 0;
    
    // Filter products
    const filteredProducts = clientLogs.by_product.filter(product => 
      !isExcludedInternalClient(product.client_name) &&
      !product.client_name?.toLowerCase().includes('listener') &&
      product.client_name !== 'EXTRA' &&
      product.client_name !== 'log_size_monitor' &&
      product.client_name !== 'rsyslog_size_history' &&
      product.client_name !== 'disk_usage_monitor' &&
      product.client_name !== 'Eventus_ABD' &&
      product.client_name !== "eventus_demo" &&
      product.client_name !== 'errorlogs' &&
      product.client_name !== 'Advisory' &&
      product.client_name !== 'rsyslog1' &&
      product.product.toLowerCase() !== 'rsyslog' &&
      product.product.toLowerCase() !== 'other' &&
      product.product.toLowerCase() !== 'error' &&
      product.product.toLowerCase() !== 'na' &&
      product.product.toLowerCase() !== 'unknown' &&
      product.product.toLowerCase() !== 'unknownproduct' &&
      product.product.toLowerCase() !== 'default' &&
      product.product !== ''
    );
    
    // Get unique product names
    const uniqueProducts = new Set();
    
    if (selectedRegion) {
      // If region is selected, count unique products in that region
      filteredProducts.forEach(product => {
        if (product.region === selectedRegion || !product.region) {
          uniqueProducts.add(product.product);
        }
      });
        } else {
      // Count all unique products across all regions
      filteredProducts.forEach(product => {
        uniqueProducts.add(product.product);
      });
    }
    
    return uniqueProducts.size;
  }, [clientLogs, selectedRegion]);
  
  // Format trend for products card
  const productTrend = useMemo(() => {
    // This would normally be calculated from historical data
    // For now, we'll use a placeholder value based on product count
    return productCount > 5 ? `+${Math.round(productCount/2)}%` : '+5%';
  }, [productCount]);
  
  // Generate product distribution data for chart
  const productDistributionData = useMemo(() => {
    if (!clientLogs || !clientLogs.by_product) return null;
    
    let filteredProducts = clientLogs.by_product.filter(product => 
      !isExcludedInternalClient(product.client_name) &&
      product.product.toLowerCase() !== 'rsyslog' &&
      product.product.toLowerCase() !== 'other' &&
      product.product.toLowerCase() !== 'error' &&
      product.product.toLowerCase() !== 'na' &&
      product.product.toLowerCase() !== 'unknown' &&
      product.product.toLowerCase() !== 'unknownproduct' &&
      product.product.toLowerCase() !== 'default' &&
      product.product !== ''
    );
      
      // Filter by region if selected
    if (selectedRegion) {
      filteredProducts = filteredProducts.filter(product => product.region === selectedRegion);
      }
      
    // Aggregate data by product
    const productMap = new Map();
    filteredProducts.forEach(product => {
      const productName = product.product;
      if (!productMap.has(productName)) {
        productMap.set(productName, 0);
      }
      productMap.set(productName, productMap.get(productName) + product.size_gb);
    });
    
    // Convert to array and sort by size
    const productArray = Array.from(productMap.entries())
      .map(([name, size]) => ({ name, size }))
      .sort((a, b) => b.size - a.size)
      .slice(0, 10); // Limit to top 10
    
    return {
      labels: productArray.map(p => p.name),
      datasets: [
        {
          label: 'Log Size (GB)',
          data: productArray.map(p => p.size),
          backgroundColor: productArray.map((_, i) => alpha(getColor(i), 0.7)),
          borderColor: productArray.map((_, i) => getColor(i)),
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [clientLogs, selectedRegion]);
  
  // Calculate RSYSLOG-only log size
  const rsyslogLogSize = useMemo(() => {
    if (!clientLogs || !clientLogs.by_product) return 0;
    
    let filteredProducts = clientLogs.by_product;
    
    filteredProducts = filteredProducts.filter(p => !isExcludedInternalClient(p.client_name));

    // Filter by region if selected
    if (selectedRegion) {
      filteredProducts = filteredProducts.filter(product => 
        product.region === selectedRegion || !product.region
      );
    }
    
    // Filter for RSYSLOG product only
    const rsyslogProducts = filteredProducts.filter(product => 
      product.product.toLowerCase() === 'rsyslog');
    
    // Sum up RSYSLOG log sizes
    return rsyslogProducts.reduce((total, product) => total + product.size_gb, 0);
  }, [clientLogs, selectedRegion]);
  
  // Memoized chart options to prevent re-renders
  const lineChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
          font: {
            size: 10,
          },
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 4,
      },
      line: {
        borderWidth: 2,
      },
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);
  
  // Update where client names are displayed in charts
  const prepareClientChartData = (data) => {
    if (!data?.by_client) return null;

    const rows = data.by_client.filter((c) => !isExcludedInternalClient(c.client_name));

    return {
      labels: rows.slice(0, 10).map(client => getFullClientName(client.client_name)),
      datasets: [{
        label: "Log Size (GB)",
        data: rows.slice(0, 10).map(client => client.size_gb),
        backgroundColor: Array(10).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  // Update product chart data
  const prepareProductChartData = (data) => {
    if (!data?.by_product) return null;

    const visibleProducts = data.by_product.filter(product => {
      if (isExcludedInternalClient(product.client_name)) return false;
      const name = (product?.product || '').trim().toLowerCase();
      return name !== '' && name !== 'unknown' && name !== 'unknownproduct';
    });

    return {
      labels: visibleProducts.slice(0, 10).map(product =>
        `${product.product} (${getFullClientName(product.client_name)})`
      ),
      datasets: [{
        label: "Log Size (GB)",
        data: visibleProducts.slice(0, 10).map(product => product.size_gb),
        backgroundColor: Array(10).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(10).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };

  // Match Clients page default: client counts use a calendar day, not a rolling 24h window.
  // Without dates, /api/client_product_count falls back to range=24h and inflates vs Clients.
  const getClientCountQueryForDashboard = useCallback(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    return { from_date: todayStr, to_date: todayStr };
  }, []);

  // Explicitly fetch clientCount data as soon as component mounts with high priority
  useEffect(() => {
    const loadClientCountData = async () => {
      try {
        console.log("Prioritizing client count data load for region:", selectedRegion);
        // Use setTimeout with 0 to push to the next event loop cycle but keep high priority
        setTimeout(async () => {
          const range = getClientCountQueryForDashboard();
          await fetchClientCount({ region: selectedRegion, ...range });
          console.log("Client count data loaded with high priority");
        }, 0);
      } catch (error) {
        console.error("Error loading clientCount data:", error);
      }
    };
    
    loadClientCountData();
  }, [fetchClientCount, selectedRegion, getClientCountQueryForDashboard]);

  // Function to specifically refresh client count data
  const refreshRegionData = useCallback(async () => {
    console.log("Explicitly refreshing client count data for region:", selectedRegion);
    try {
      const range = getClientCountQueryForDashboard();
      const data = await fetchClientCount({ region: selectedRegion, ...range });
      console.log("Refreshed client count data:", data);
      return true;
    } catch (error) {
      console.error("Error refreshing client count data:", error);
      return false;
    }
  }, [fetchClientCount, selectedRegion, getClientCountQueryForDashboard]);

  // Update the chart refresh button click handler
  const handleRegionChartRefresh = useCallback(() => {
    refreshRegionData();
  }, [refreshRegionData]);
  
  // Align with Clients page: same parent detection, exclusions, and canonical dedupe.
  // Prefer /api/clients when loaded; otherwise derive from client_product_count keys.
  const accurateClientCount = useMemo(() => {
    if (clients && clients.length > 0) {
      return countClientsParentViewDeduped(clients, clientLicenses, clientLogs);
    }
    return countFromClientProductCountRegions(
      clientCount,
      selectedRegion,
      clientLicenses,
      clientLogs
    );
  }, [clients, clientLicenses, clientLogs, clientCount, selectedRegion]);
  
  // Dashboard Header section
  const DashboardHeader = (
    <Box
      sx={{
        mb: 4,
        borderRadius: '20px',
        padding: { xs: 2, md: 3 },
        backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5,
          }}
        >
          {selectedRegion ? `${selectedRegion} Region` : 'Dashboard Overview'}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {selectedRegion 
            ? `Monitoring ${selectedRegion} region performance`
            : 'Monitor system performance and client logs'}
        </Typography>
      </Box>
      
      <Box display="flex" gap={2} alignItems="center">
        <Button
          variant="outlined"
          onClick={handleRegionMenuOpen}
          endIcon={<ExpandMoreIcon />}
          startIcon={<RegionIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 'medium',
            borderColor: alpha(theme.palette.primary.main, 0.3),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          {selectedRegion || 'All Regions'}
        </Button>
        
        <Tooltip title="Refresh Data">
          <IconButton
            onClick={handleRefresh}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <DashboardContainer
      title="Loading Dashboard Data"
      loadingKeys={['summary', 'alerts', 'systemMetrics', 'clientLogs', 'clientCount']}
      onRefresh={handleRefresh}
    >
      {/* Dashboard Header */}
      {DashboardHeader}

      {/* Dashboard Content */}
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          {/* Only show hosts card for users with VIEW_HOSTS permission */}
          {hasPermission('VIEW_HOSTS') && (
            <Grid item xs={12} sm={6} md={hasPermission('VIEW_ALERTS') ? 2.4 : 3}>
              <ModernMetricCard
                title="Listeners"
                value={summary.total_hosts || 0}
                subtitle={`${summary.active_services || 0} active`}
                icon={ServerIcon}
                trend="up"
                trendValue={`${activeServicesPercentage}%`}
                color="primary"
                onClick={navigateToHosts}
              />
            </Grid>
          )}
          
          {/* Show clients card for users with VIEW_CLIENTS permission */}
          {hasPermission('VIEW_CLIENTS') && (
            <Grid item xs={12} sm={6} md={hasPermission('VIEW_HOSTS') && hasPermission('VIEW_ALERTS') ? 2.4 : 3}>
              <ModernMetricCard
                title="Clients"
                value={accurateClientCount || 0}
                subtitle={selectedRegion ? `In ${selectedRegion}` : "In all regions"}
                icon={BusinessIcon}
                trend="up"
                trendValue="+12%"
                color="secondary"
                onClick={navigateToClients}
              />
            </Grid>
          )}
          
          {/* Show products card for users with VIEW_CLIENTS permission (since products are part of clients data) */}
          {hasPermission('VIEW_CLIENTS') && (
            <Grid item xs={12} sm={6} md={hasPermission('VIEW_HOSTS') && hasPermission('VIEW_ALERTS') ? 2.4 : 3}>
              <ModernMetricCard
                title="Products"
                value={productCount || 0}
                subtitle={selectedRegion ? `In ${selectedRegion}` : "In all regions"}
                icon={ProductIcon}
                trend="up"
                trendValue={productTrend}
                color="quaternary"
                onClick={navigateToProducts}
              />
            </Grid>
          )}
          
          {/* Show alerts card for users with VIEW_ALERTS permission */}
          {hasPermission('VIEW_ALERTS') && (
            <Grid item xs={12} sm={6} md={hasPermission('VIEW_HOSTS') ? 2.4 : 3}>
              <ModernMetricCard
                title="Alerts"
                value={filteredAlerts.length || 0}
                subtitle={selectedRegion ? `In ${selectedRegion}` : "Active alerts"}
                icon={WarningIcon}
                trend="down"
                trendValue="-2"
                color="error"
                onClick={navigateToAlerts}
              />
            </Grid>
          )}
        </Grid>

        {/* Charts Row 1 */}
        <Grid container spacing={3} mb={4}>
          {/* Only show CPU chart for users with VIEW_HOSTS permission */}
          {hasPermission('VIEW_HOSTS') && (
            <Grid item xs={12} md={8}>
              <ModernChartCard
                title="CPU Usage Trends"
                subtitle={selectedRegion ? `CPU utilization in ${selectedRegion}` : "CPU utilization across all hosts"}
                chart={systemMetricsData ? (
                  <Line
                    data={systemMetricsData}
                    options={lineChartOptions}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="text.secondary">No data available</Typography>
                  </Box>
                )}
              />
            </Grid>
          )}
          
          {/* Only show product distribution for users with VIEW_CLIENTS permission and who are not finance users */}
          {hasPermission('VIEW_CLIENTS') && !hasFinanceRole && (  
            <Grid item xs={12} md={hasPermission('VIEW_HOSTS') ? 4 : 12}>
              <ModernChartCard
                title="Product Distribution"
                subtitle={selectedRegion ? `Products in ${selectedRegion}` : "Top products by log size"}
                chart={productDistributionData ? (
                  <Bar
                    data={productDistributionData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          backgroundColor: theme.palette.background.paper,
                          titleColor: theme.palette.text.primary,
                          bodyColor: theme.palette.text.secondary,
                          borderColor: alpha(theme.palette.text.primary, 0.1),
                          borderWidth: 1,
                          padding: 8,
                          boxPadding: 4,
                        },
                      },
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Log Size (GB)',
                            color: theme.palette.text.secondary,
                            font: {
                              size: 12,
                            },
                          },
                          grid: {
                            color: alpha(theme.palette.text.secondary, 0.05),
                            drawBorder: false,
                          },
                          ticks: {
                            color: theme.palette.text.secondary,
                          },
                        },
                        y: {
                          grid: {
                            display: false,
                            drawBorder: false,
                          },
                          ticks: {
                            color: theme.palette.text.secondary,
                          },
                        },
                      },
                      animation: false, // Disable animations for better performance
                    }}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="text.secondary">No product data available</Typography>
                  </Box>
                )}
              />
            </Grid>
          )}
        </Grid>
        
        {/* Charts Row 2 */}
        <Grid container spacing={3}>
          {/* Show region distribution chart for everyone */}
          <Grid item xs={12} md={hasPermission('VIEW_HOSTS') ? 6 : 12}>
            <ModernChartCard
              title="Region Distribution"
              subtitle="Client distribution by region"
              chart={
                isRegionDistributionLoading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column">
                    <CircularProgress size={36} thickness={4} sx={{ mb: 2, color: theme.palette.primary.main }} />
                    <Typography color="text.secondary" variant="body2">Loading region data...</Typography>
                  </Box>
                ) : regionDistributionData ? (
                  <Doughnut
                    data={regionDistributionData}
                    options={doughnutOptions}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column">
                    <Typography color="text.secondary" mb={1}>No region data available</Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<RefreshIcon />} 
                      onClick={handleRegionChartRefresh}
                      sx={{ mt: 1 }}
                    >
                      Refresh Data
                    </Button>
                  </Box>
                )
              }
              actions={
                <IconButton 
                  size="small"
                  onClick={handleRegionChartRefresh}
                  disabled={isRegionDistributionLoading}
                  sx={{
                    color: theme.palette.primary.main,
                    opacity: isRegionDistributionLoading ? 0.5 : 1,
                  }}
                >
                  {isRegionDistributionLoading ? 
                    <CircularProgress size={16} thickness={4} color="inherit" /> : 
                    <RefreshIcon fontSize="small" />
                  }
                </IconButton>
              }
            />
          </Grid>

          {/* Only show Memory Usage for users with VIEW_HOSTS permission */}
          {hasPermission('VIEW_HOSTS') && (
            <Grid item xs={12} md={6}>
              <ModernChartCard
                title="Memory Usage"
                subtitle={selectedRegion ? `Memory usage in ${selectedRegion}` : "Memory usage across hosts"}
                chart={
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="text.secondary">Coming soon</Typography>
                  </Box>
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>
      
      {/* Region Menu */}
      <Menu
        anchorEl={regionMenuAnchor}
        open={Boolean(regionMenuAnchor)}
        onClose={handleRegionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '12px',
            minWidth: 180,
            '& .MuiList-root': {
              padding: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => handleRegionSelect('')}
          sx={{
            borderRadius: '8px',
            py: 1,
            my: 0.5,
            backgroundColor: selectedRegion === '' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <RegionIcon
            fontSize="small"
            sx={{ mr: 1.5, color: theme.palette.primary.main }}
          />
          <Typography variant="body2">All Regions</Typography>
        </MenuItem>
        <Divider sx={{ my: 1, opacity: 0.1 }} />
        {regions && regions.map((region) => (
          <MenuItem
            key={region}
            onClick={() => handleRegionSelect(region)}
            sx={{
              borderRadius: '8px',
              py: 1,
              my: 0.5,
              backgroundColor: selectedRegion === region ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <RegionIcon
              fontSize="small"
              sx={{ mr: 1.5, color: theme.palette.primary.main }}
            />
            <Typography variant="body2">{region} Region</Typography>
          </MenuItem>
        ))}
      </Menu>
    </DashboardContainer>
  );
}